/**
 * ViewControls Component
 * Main controls bar for changing view mode, columns, and settings
 */

import React from 'react';
import clsx from 'clsx';
import { useViewStore } from '../../store/viewStore';
import { ViewToggle } from './ViewToggle';
import { ColumnsSelector } from './ColumnsSelector';
import { SettingsPopover } from './SettingsPopover';
import type { ViewControlsProps } from './types';
import type { UserControlsConfig } from '../../types';

interface ExtendedViewControlsProps extends ViewControlsProps {
  config?: UserControlsConfig;
}

export const ViewControls: React.FC<ExtendedViewControlsProps> = ({
  show = true,
  position = 'top',
  config,
}) => {
  const {
    displayMode,
    gridColumns,
    showTrustScore,
    showTags,
    showActions,
    compactMode,
    setDisplayMode,
    setGridColumns,
    toggleTrustScore,
    toggleTags,
    toggleActions,
    toggleCompactMode,
    resetToDefaults,
  } = useViewStore();

  // Don't render if controls are disabled
  if (!show || !config?.showControlsBar) {
    return null;
  }

  const allowViewChange = config.allowViewChange !== false;
  const allowColumnsChange = config.allowColumnsChange !== false;
  const showSettingsMenu = config.showSettingsMenu !== false;
  
  const availableViews = config.availableViews || ['list', 'cards', 'text'];
  const availableColumns = config.availableColumns || [1, 2, 3];
  const availableSettings = config.availableSettings || ['trustscore', 'tags', 'actions', 'compact'];

  // Don't show columns selector in list or text mode
  const isColumnsDisabled = displayMode === 'list' || displayMode === 'text';

  return (
    <div className={clsx('ai360-view-controls', `ai360-view-controls--${position}`)}>
      <div className="ai360-view-controls-container">
        {/* Left side: View toggle */}
        {allowViewChange && (
          <div className="ai360-view-controls-section">
            <ViewToggle
              currentView={displayMode}
              availableViews={availableViews}
              onChange={setDisplayMode}
            />
          </div>
        )}

        {/* Right side: Columns and settings */}
        <div className="ai360-view-controls-section ai360-view-controls-section--right">
          {allowColumnsChange && (
            <ColumnsSelector
              currentColumns={gridColumns}
              availableColumns={availableColumns}
              onChange={setGridColumns}
              disabled={isColumnsDisabled}
            />
          )}

          {showSettingsMenu && (
            <SettingsPopover
              showTrustScore={showTrustScore}
              showTags={showTags}
              showActions={showActions}
              compactMode={compactMode}
              availableSettings={availableSettings}
              onToggleTrustScore={toggleTrustScore}
              onToggleTags={toggleTags}
              onToggleActions={toggleActions}
              onToggleCompact={toggleCompactMode}
              onReset={resetToDefaults}
            />
          )}
        </div>
      </div>
    </div>
  );
};
